/* eslint-disable functional/immutable-data */

import type { ReactNode } from 'react';
import React from 'react';
import type { ImageSourcePropType, ImageStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  IllusSupplyChain,
  Image,
  theme,
  TitleText,
  TYPE_CONFIG,
  useResponsive,
} from '@sweetgreen/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LandingContent = (props: LandingContentProps) => {
  const { children } = props;

  return <View style={styles.content}>{children}</View>;
};

// ─────────────────────────────────────────────────────────────────────────────

const LandingContentIllustration = (props: LandingContentIllustrationProps) => {
  const { source = IllusSupplyChain, style } = props;

  return (
    <Image
      style={[styles.image, style]}
      source={source}
      accessibilityIgnoresInvertColors
      resizeMode="cover"
    />
  );
};

const LandingContentTitle = (props: LandingContentTitleProps) => {
  const { children } = props;

  const { match } = useResponsive();

  return (
    <TitleText
      style={[styles.title, match([undefined, styles.titleSM])]}
      size={5}
    >
      {children}
    </TitleText>
  );
};

const LandingContentSubtitle = (props: LandingContentSubitleProps) => {
  const { children } = props;

  return <BodyText style={styles.subtitle}>{children}</BodyText>;
};

// ─────────────────────────────────────────────────────────────────────────────

LandingContent.Illustration = LandingContentIllustration;
LandingContent.Title = LandingContentTitle;
LandingContent.Subtitle = LandingContentSubtitle;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['10'],
    paddingBottom: theme.spacing['6'],
  },
  image: {
    width: 280,
    height: 166,
    marginBottom: theme.spacing['4'],
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  titleSM: {
    // We override fluid text styles because we need static 32px styles starting from
    // the `SM` breakpoint, which the present fluid text styles cannot provide.
    ...TYPE_CONFIG.TITLE['32'],
  },
  subtitle: {
    color: theme.colors.CHARCOAL,
    textAlign: 'center',
    marginBottom: theme.spacing['4'],

    // We override fluid text styles because we need static 18px styles,
    // which the present fluid text styles cannot provide.
    ...TYPE_CONFIG.BODY['18'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LandingContentTitleProps = {
  readonly children: string;
};

type LandingContentSubitleProps = {
  readonly children: string;
};

type LandingContentIllustrationProps = {
  readonly source?: ImageSourcePropType;
  readonly style?: ImageStyle;
};

type LandingContentProps = {
  readonly children?: ReactNode;
};
